import { ChevronDown, ChevronRight, Plus, RefreshCw, Search, Upload } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/Button';
import { useAuth } from '../../../hooks/useAuth';
import { DailyTaskTemplateCard } from './DailyTaskTemplateCard';
import { EditDailyTaskTemplate } from './EditDailyTaskTemplate';
import { dailyTaskTemplateService } from '../services/dailyTaskTemplateService';
import { importDailyTaskTemplates } from '../services/dailyTaskTemplateImportService';
import {
  DAILY_TASK_CATEGORY_LABELS,
  DAILY_TASK_CATEGORY_ORDER,
} from '../services/dailyTaskTemplateTypes';
import type {
  DailyTaskCategory,
  DailyTaskTemplateFirestore,
} from '../services/dailyTaskTemplateTypes';

type FormData = Omit<DailyTaskTemplateFirestore, 'id' | 'createdAt' | 'updatedAt'>;

export const DailyTaskTemplates = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<DailyTaskTemplateFirestore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [editingTemplate, setEditingTemplate] = useState<DailyTaskTemplateFirestore | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dailyTaskTemplateService.getAll();
      setTemplates(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.emoji.includes(q) ||
        t.ocdSubtypeTags.some((tag) => tag.includes(q)) ||
        t.dayKindTags.some((tag) => tag.includes(q)),
    );
  }, [templates, search]);

  const groupedByCategory = useMemo(() => {
    const groups = new Map<DailyTaskCategory, DailyTaskTemplateFirestore[]>();
    for (const cat of DAILY_TASK_CATEGORY_ORDER) {
      const items = filteredTemplates.filter((t) => t.category === cat);
      if (items.length > 0) {
        groups.set(cat, items);
      }
    }
    return groups;
  }, [filteredTemplates]);

  const toggleCategory = (cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  };

  const handleSaveNew = async (data: FormData) => {
    await dailyTaskTemplateService.create(data);
    setIsCreating(false);
    await loadTemplates();
  };

  const handleSaveEdit = async (data: FormData) => {
    if (!editingTemplate?.id) return;
    await dailyTaskTemplateService.update({ ...data, id: editingTemplate.id } as DailyTaskTemplateFirestore);
    setEditingTemplate(null);
    await loadTemplates();
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await dailyTaskTemplateService.remove(templateId);
      await loadTemplates();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    e.target.value = '';
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        alert('Could not parse the file — make sure it is valid JSON.');
        return;
      }

      const result = await importDailyTaskTemplates(parsed);
      alert(
        `Imported ${result.total} template${result.total !== 1 ? 's' : ''} ` +
          `(${result.created} created, ${result.updated} updated).`,
      );
      await loadTemplates();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const handleResetDailyTasks = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      'Are you sure you want to reset your daily tasks? This will delete all generated tasks and pending queue items. This cannot be undone.',
    );
    if (!confirmed) return;

    setIsResetting(true);
    try {
      await dailyTaskTemplateService.resetUserDailyTasks(user.uid);
      alert('Daily tasks reset successfully.');
    } catch (error) {
      console.error('Failed to reset daily tasks:', error);
      alert('Failed to reset daily tasks. Check the console for details.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Daily Task Templates</h2>
          <p className="text-sm text-gray-500 mt-1">
            {templates.length} template{templates.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleResetDailyTasks}
            disabled={isResetting}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResetting ? 'Resetting...' : 'Reset My Daily Tasks'}
          </button>
          <Button
            variant="outline"
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={loadTemplates}
            loading={loading}
          >
            Refresh
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button
            variant="outline"
            size="sm"
            icon={<Upload size={14} />}
            onClick={() => fileInputRef.current?.click()}
            loading={isImporting}
          >
            Import JSON
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setIsCreating(true)}
          >
            New Template
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title, tags..."
          className="w-full pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-sm">
            {search ? 'No templates match your search.' : 'No templates yet. Create your first one!'}
          </p>
        </div>
      )}

      {/* Grouped list */}
      {!loading && (
        <div className="space-y-4">
          {Array.from(groupedByCategory.entries()).map(([category, items]) => {
            const isCollapsed = collapsedCategories.has(category);
            return (
              <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Category header */}
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isCollapsed ? (
                      <ChevronRight size={16} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-400" />
                    )}
                    <h3 className="text-sm font-semibold text-gray-700">
                      {DAILY_TASK_CATEGORY_LABELS[category]}
                    </h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                      {items.length}
                    </span>
                  </div>
                </button>

                {/* Items */}
                {!isCollapsed && (
                  <div className="px-4 pb-3 space-y-2">
                    {items.map((template) => (
                      <DailyTaskTemplateCard
                        key={template.id}
                        template={template}
                        onEdit={() => setEditingTemplate(template)}
                        onDelete={() => template.id && handleDelete(template.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {isCreating && (
        <EditDailyTaskTemplate
          onSave={handleSaveNew}
          onClose={() => setIsCreating(false)}
        />
      )}

      {/* Edit modal */}
      {editingTemplate && (
        <EditDailyTaskTemplate
          template={editingTemplate}
          onSave={handleSaveEdit}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </div>
  );
};
